import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { theme as antdTheme } from 'antd';
import { useAppStore } from '../stores/useAppStore';
import type { ThemeMode } from '../stores/slices/settingsSlice';

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function subscribeToSystemTheme(callback: () => void) {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function getPrefersReducedMotion(): boolean {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function subscribeToReducedMotion(callback: () => void) {
  const mq = window.matchMedia(REDUCED_MOTION_QUERY);
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

export function useTheme() {
  const themeMode = useAppStore((s) => s.themeMode);
  const setThemeMode = useAppStore((s) => s.setThemeMode);

  const systemTheme = useSyncExternalStore(subscribeToSystemTheme, getSystemTheme, () => 'light' as const);

  const resolved = themeMode === 'system' ? systemTheme : themeMode;

  // «Отключить анимацию» в ОС: глобальное правило в _reset.scss сжимает все
  // анимации до 0.01ms, а всплывашки antd (Select, Dropdown, Tooltip…) выравниваются
  // в фазе подготовки анимации — без неё попап застревает за экраном (-1000vw/-1000vh).
  // Поэтому при reduced motion выключаем анимации antd штатно, через токен motion.
  const prefersReducedMotion = useSyncExternalStore(subscribeToReducedMotion, getPrefersReducedMotion, () => false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);

  const isDark = resolved === 'dark';

  const antdThemeConfig = useMemo(
    () => ({
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: '#1e3a8a',
        motion: !prefersReducedMotion,
        fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, sans-serif',
        ...(isDark && {
          colorBgContainer: '#1a1d2e',
          colorBgElevated: '#1f2233',
          colorBorderSecondary: '#2d3348',
          colorText: '#e4e4e7',
          colorTextSecondary: '#a1a1aa',
        }),
      },
      ...(isDark && {
        components: {
          Table: {
            headerBg: '#1f2233',
            headerColor: '#c4c4cc',
            rowHoverBg: '#2d3350',
            borderColor: '#2d3348',
            rowSelectedBg: '#1a2744',
            rowSelectedHoverBg: '#1e2f52',
            colorBgContainer: '#1a1d2e',
            headerSortActiveBg: '#252942',
            fixedHeaderSortActiveBg: '#252942',
          },
        },
      }),
    }),
    [isDark, prefersReducedMotion],
  );

  return { isDark, themeMode, setThemeMode, resolved, antdThemeConfig };
}

export type { ThemeMode };
